<!--
  GalleryViewer — a read-only modal grid of photos. `open` controls visibility
  (v-model:open), `photos` is the list, each item needs { id, s3_key, caption? }.
  Clicking a cell opens the full image in a new tab.
-->
<template>
  <div v-if="open" class="gv-overlay" @click.self="close">
    <div class="gv-content">
      <div class="gv-head">
        <h3>{{ title }}</h3>
        <button type="button" class="gv-close" title="Close" @click="close">✕</button>
      </div>
      <div v-if="loading" class="gv-muted">Loading photos…</div>
      <div v-else-if="!photos.length" class="gv-muted">No photos.</div>
      <div v-else class="gv-grid">
        <a v-for="ph in photos" :key="ph.id" :href="photoUrl(ph.s3_key)" target="_blank" rel="noopener" class="gv-cell">
          <img :src="photoUrl(ph.s3_key)" :alt="ph.caption || 'photo'" loading="lazy" />
        </a>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { photoUrl } from '@/composables/useAdminApi'

defineProps<{
  open: boolean
  title?: string
  loading?: boolean
  photos: Array<{ id: number | string; s3_key: string; caption?: string }>
}>()
const emit = defineEmits<{ (e: 'update:open', v: boolean): void }>()
function close() { emit('update:open', false) }
</script>

<style scoped>
.gv-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.6); display: flex; align-items: center; justify-content: center; z-index: 1000; padding: 1rem; }
.gv-content { background: var(--bg-surface); color: var(--text-primary); border-radius: var(--radius-md, 10px); border: 1px solid var(--border-default); padding: 1.25rem; max-width: 760px; width: 90vw; }
.gv-head { display: flex; align-items: center; justify-content: space-between; margin-bottom: 0.75rem; }
.gv-head h3 { margin: 0; }
.gv-close { background: transparent; border: none; color: var(--text-muted); cursor: pointer; font-size: 1rem; padding: 0.2rem 0.4rem; border-radius: var(--radius-sm); }
.gv-close:hover { color: var(--text-primary); background: var(--bg-elevated); }
.gv-muted { color: var(--text-muted); font-size: 0.8rem; }
.gv-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(140px, 1fr)); gap: 0.6rem; max-height: 70vh; overflow-y: auto; }
.gv-cell { display: block; border-radius: var(--radius-sm); overflow: hidden; background: var(--bg-elevated); aspect-ratio: 1 / 1; }
.gv-cell img { width: 100%; height: 100%; object-fit: cover; display: block; }
</style>
